/**
 * File: src/components/AthleteForm.js
 * Description: Individual Athlete registration and modification sub-form UI context layer.
 * Manages reactive data state tracking, validation rules, and automated age standard preview renderings.
 * Version: 3.2.0
 *
 * @format
 */

import { useState, useEffect } from '@wordpress/element';

const AthleteForm = ({ onSave, onCancel, initialData, clubs = {}, countries = {}, ageStandard = 'standard_hs' }) => {
	const [formData, setFormData] = useState({
		firstName: '',
		middleName: '',
		lastName: '',
		sexCode: 'M',
		dateOfBirth: '',
		clubCode: '',
		clubName: '',
		countryCode: 'JM',
		countryName: 'Jamaica',
	});

	// Helper to find a Name by its Code within the standard mapping dictionary structure
	const findNameByCode = (code, dictionary) => {
		if (!code) return '';
		const targetCode = code.toUpperCase().trim();
		return Object.keys(dictionary).find((name) => dictionary[name]?.toUpperCase().trim() === targetCode) || '';
	};

	// Mirrors precise logic found in includes/amm-helpers.php
	const computeClassAndAge = (dob, sexCode) => {
		if (!dob) return { intClass: '—', hsClass: '—' };

		const parts = dob.split('-');
		if (parts.length !== 3) return { intClass: '—', hsClass: '—' };

		const bYear = parseInt(parts[0], 10);
		const bMonth = parseInt(parts[1], 10);
		const bDay = parseInt(parts[2], 10);

		// Dynamically pull the exact context competition year
		const currentYear = new Date().getFullYear();

		// 1. INTERNATIONAL STANDARD SCHEMA MATCHING
		const intAge = currentYear - bYear;
		let intClass = 'Senior';

		if (intAge < 14) intClass = 'Under 14';
		else if (intAge === 14) intClass = 'Under 15';
		else if (intAge >= 15 && intAge <= 16) intClass = 'Under 17';
		else if (intAge === 17) intClass = 'Under 18';
		else if (intAge >= 18 && intAge <= 19) intClass = 'Under 20';

		// 2. HIGH SCHOOL STANDARD (SEPTEMBER AGE CUT-OFF) SCHEMA MATCHING
		let septemberAge = currentYear - bYear;

		// If born in September (month 9) or later, stay in the younger bracket
		if (bMonth > 9 || (bMonth === 9 && bDay > 0)) {
			septemberAge--;
		}

		let hsClass = 'Senior';
		const isFemale = sexCode?.toUpperCase() === 'F';

		if (isFemale) {
			if (septemberAge <= 13) hsClass = 'Class 4';
			else if (septemberAge <= 15) hsClass = 'Class 3';
			else if (septemberAge <= 17) hsClass = 'Class 2';
			else if (septemberAge <= 19) hsClass = 'Class 1';
		} else {
			// Male Divisions
			if (septemberAge <= 14) hsClass = 'Class 3';
			else if (septemberAge <= 16) hsClass = 'Class 2';
			else if (septemberAge <= 19) hsClass = 'Class 1';
		}

		return { intClass, hsClass };
	};

	// Hydrate form on initialization and systematically reconcile names with map definitions
	useEffect(() => {
		if (initialData) {
			const activeClubCode = (initialData.clubCode || initialData.club || '').toUpperCase().trim();
			const activeCountryCode = (initialData.countryCode || 'JM').toUpperCase().trim();

			// Resolve names directly from the loaded meta maps to correct database overrides
			const resolvedClubName = findNameByCode(activeClubCode, clubs);
			const resolvedCountryName = findNameByCode(activeCountryCode, countries);

			setFormData({
				firstName: initialData.firstName || '',
				middleName: initialData.middleName || '',
				lastName: initialData.lastName || '',
				sexCode: initialData.sexCode || initialData.gender || 'M',
				dateOfBirth: initialData.dateOfBirth || '',
				clubCode: activeClubCode,
				clubName: resolvedClubName,
				countryCode: activeCountryCode,
				countryName: resolvedCountryName,
			});
		}
	}, [initialData, clubs, countries]);

	const handleClubChange = (e) => {
		const selectedCode = e.target.value.toUpperCase().trim();
		const selectedName = findNameByCode(selectedCode, clubs);

		setFormData({
			...formData,
			clubCode: selectedCode,
			clubName: selectedName,
		});
	};

	const handleCountryChange = (e) => {
		const selectedCode = e.target.value.toUpperCase().trim();
		const selectedName = findNameByCode(selectedCode, countries);

		setFormData({
			...formData,
			countryCode: selectedCode,
			countryName: selectedName,
		});
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		// Final check step to enforce clean value pairing strings regardless of field interactions
		const finalizedClubName = findNameByCode(formData.clubCode, clubs);
		const finalizedCountryName = findNameByCode(formData.countryCode, countries);

		const transmissionPayload = {
			...formData,
			clubName: finalizedClubName,
			countryName: finalizedCountryName,
		};

		onSave(transmissionPayload);
	};

	const classifications = computeClassAndAge(formData.dateOfBirth, formData.sexCode);

	return (
		<form onSubmit={handleSubmit} className="athlete-form-container row g-3">
			<div className="col-12 m-0 p-0">
				<div className="row g-3">
					<div className="col-md-4">
						<label className="form-label small fw-bold text-dark">First Name</label>
						<input type="text" className="form-control form-control-sm text-uppercase" required value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
					</div>
					<div className="col-md-4">
						<label className="form-label small fw-bold text-dark">Middle Name</label>
						<input type="text" className="form-control form-control-sm text-uppercase" value={formData.middleName} onChange={(e) => setFormData({ ...formData, middleName: e.target.value })} />
					</div>
					<div className="col-md-4">
						<label className="form-label small fw-bold text-dark">Last Name</label>
						<input type="text" className="form-control form-control-sm text-uppercase" required value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
					</div>
				</div>
			</div>

			<div className="col-12 m-0 mt-3 p-0">
				<div className="row g-3">
					<div className="col-md-3">
						<label className="form-label small fw-bold text-dark">Sex Category</label>
						<select className="form-select form-select-sm" value={formData.sexCode} onChange={(e) => setFormData({ ...formData, sexCode: e.target.value })}>
							<option value="M">Male (M)</option>
							<option value="F">Female (F)</option>
						</select>
					</div>
					<div className="col-md-3">
						<label className="form-label small fw-bold text-dark">Date of Birth</label>
						<input type="date" className="form-control form-control-sm" required value={formData.dateOfBirth} onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })} />
					</div>
					<div className="col-md-6">
						<label className="form-label small fw-bold text-muted">Auto Classification Matrix</label>
						<div className="form-control form-control-sm bg-light d-flex gap-2 align-items-center justify-content-between border-dashed" style={{ minHeight: '31px' }}>
							<span className="small text-truncate">
								INTL: <strong className="text-primary text-uppercase">{classifications.intClass}</strong>
							</span>
							<span className="vr text-muted my-1"></span>
							<span className="small text-truncate">
								HS: <strong className="text-success text-uppercase">{classifications.hsClass}</strong>
							</span>
						</div>
					</div>
				</div>
			</div>

			<div className="col-12 m-0 mt-3 p-0">
				<div className="row g-3">
					<div className="col-md-6">
						<label className="form-label small fw-bold text-dark">Affiliated Club / Institution</label>
						<select className="form-select form-select-sm" value={formData.clubCode} onChange={handleClubChange}>
							<option value="">Independent / Unattached</option>
							{Object.entries(clubs).map(([name, code]) => (
								<option key={code} value={code.toUpperCase().trim()}>
									{name}
								</option>
							))}
						</select>
					</div>
					<div className="col-md-6">
						<label className="form-label small fw-bold text-dark">Representing Country</label>
						<select className="form-select form-select-sm" required value={formData.countryCode} onChange={handleCountryChange}>
							<option value="">Select country...</option>
							{Object.entries(countries).map(([name, code]) => (
								<option key={code} value={code.toUpperCase().trim()}>
									{name}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			{/* Form Control Layer Buttons */}
			<div className="mt-4 d-flex justify-content-end gap-2 pt-3 border-top">
				<button type="button" className="btn btn-sm btn-light border px-3" onClick={onCancel}>
					Cancel
				</button>
				<button type="submit" className="btn btn-sm btn-primary px-4 shadow-sm">
					{initialData ? 'Apply Updates' : 'Commit Entry'}
				</button>
			</div>
		</form>
	);
};

export default AthleteForm;
